import lookForBinder from '../lookforbinder.js';
import createNodeHandler from './_createnodehandler.js';
import createObjectHandler from './_createobjecthandler.js';
import triggerOne from '../trigger/_triggerone.js';
import addListener from '../on/_addlistener.js';
import debounce from '../_helpers/debounce.js';
import forEach from '../_helpers/foreach.js';
import assign from '../_helpers/assign.js';
import is from '../_helpers/is.js';
var spaceReg = /\s+/;
export default function bindSingleNode(object, _ref) {
  var givenBinder = _ref.binder,
    key = _ref.key,
    $nodes = _ref.$nodes,
    node = _ref.node,
    eventOptions = _ref.eventOptions,
    propDef = _ref.propDef;
  var silent = eventOptions.silent,
    getValueOnBind = eventOptions.getValueOnBind,
    setValueOnBind = eventOptions.setValueOnBind,
    _eventOptions$debounc = eventOptions.debounceSetValue,
    debounceSetValue = _eventOptions$debounc === void 0 ? true : _eventOptions$debounc,
    _eventOptions$debounc2 = eventOptions.debounceGetValue,
    debounceGetValue = _eventOptions$debounc2 === void 0 ? true : _eventOptions$debounc2,
    _eventOptions$debounc3 = eventOptions.debounceSetValueOnBind,
    debounceSetValueOnBind = _eventOptions$debounc3 === void 0 ? false : _eventOptions$debounc3,
    _eventOptions$debounc4 = eventOptions.debounceGetValueOnBind,
    debounceGetValueOnBind = _eventOptions$debounc4 === void 0 ? false : _eventOptions$debounc4,
    _eventOptions$debounc5 = eventOptions.debounceSetValueDelay,
    debounceSetValueDelay = _eventOptions$debounc5 === void 0 ? 0 : _eventOptions$debounc5,
    _eventOptions$debounc6 = eventOptions.debounceGetValueDelay,
    debounceGetValueDelay = _eventOptions$debounc6 === void 0 ? 0 : _eventOptions$debounc6,
    _eventOptions$useExac = eventOptions.useExactBinder,
    useExactBinder = _eventOptions$useExac === void 0 ? false : _eventOptions$useExac;
  // create bindings array in property definition object
  propDef.bindings = propDef.bindings || [];
  var bindings = propDef.bindings;
  var bindingOptions = {
    self: object,
    key: key,
    // the binder's functions get the current value, not the one at bind time
    get value() {
      return propDef.value;
    },
    $nodes: $nodes,
    node: node
  };
  var isUndefined = typeof propDef.value === 'undefined';
  var objectHandler;
  var nodeHandler;
  var syncNodeHandler;
  var debouncedNodeHandler;
  var debouncedObjectHandler;
  // what the node showed when a program write was scheduled, while that write waits
  var pendingWrite = null;
  // the given binder extends the node's default binder, unless useExactBinder is on
  var defaultBinder = useExactBinder ? null : lookForBinder(node);
  var binder = defaultBinder ? assign({}, defaultBinder, givenBinder) : givenBinder;
  var getValue = binder.getValue,
    setValue = binder.setValue,
    on = binder.on,
    initialize = binder.initialize;
  var readNode = function readNode() {
    return getValue == null ? void 0 : getValue.call(node, assign({}, bindingOptions, {
      previousValue: propDef.value
    }));
  };
  // call binder.initialize
  if (initialize) {
    initialize.call(node, bindingOptions);
  }
  // add needed event handlers to given node when getValue is given
  if (getValue) {
    syncNodeHandler = createNodeHandler({
      object: object,
      key: key,
      node: node,
      propDef: propDef,
      binder: binder,
      bindingOptions: bindingOptions
    });
    if (debounceGetValue || debounceGetValueOnBind) {
      debouncedNodeHandler = debounce(syncNodeHandler, debounceGetValueDelay);
    }
    if (debounceGetValue) {
      nodeHandler = debouncedNodeHandler;
    } else {
      nodeHandler = syncNodeHandler;
    }
    // TODO: Throw error when "on" and maybe other binder properties has wrong type
    if (typeof on === 'function') {
      on.call(node, nodeHandler, bindingOptions);
    } else if (typeof on === 'string') {
      // input from the node is newer than a program write still waiting for its debounce, so that write is dropped;
      // an event that leaves the node as it was (a Tab keyup on a checkbox) is not input, and the write goes ahead
      var handleNode = nodeHandler;
      nodeHandler = function nodeEventHandler() {
        if (pendingWrite) {
          var _debouncedObjectHandl;
          if (is(readNode(), pendingWrite.shown)) {
            return;
          }
          (_debouncedObjectHandl = debouncedObjectHandler) == null || _debouncedObjectHandl.cancel();
        }
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }
        return handleNode.apply(this, args);
      };
      forEach(on.split(spaceReg), function (evtName) {
        return node.addEventListener(evtName, nodeHandler);
      });
    }
    if (isUndefined && getValueOnBind !== false || getValueOnBind === true) {
      if (debounceGetValueOnBind) {
        debouncedNodeHandler();
      } else {
        syncNodeHandler();
      }
    }
    isUndefined = typeof propDef.value === 'undefined';
  }
  // add needed event handlers to the object when setValue is given
  if (setValue) {
    var writeNode = createObjectHandler({
      node: node,
      propDef: propDef,
      binder: binder,
      bindingOptions: bindingOptions,
      eventOptions: eventOptions
    });
    var syncObjectHandler = function syncObjectHandler(handlerEventOptions) {
      pendingWrite = null;
      writeNode(handlerEventOptions);
    };
    if (debounceSetValue || debounceSetValueOnBind) {
      var debouncedWrite = debounce(syncObjectHandler, debounceSetValueDelay);
      debouncedObjectHandler = assign(function (handlerEventOptions) {
        if (!pendingWrite && getValue) {
          pendingWrite = {
            shown: readNode()
          };
        }
        debouncedWrite(handlerEventOptions);
      }, {
        cancel: function cancel() {
          pendingWrite = null;
          debouncedWrite.cancel();
        }
      });
    }
    if (debounceSetValue) {
      objectHandler = debouncedObjectHandler;
    } else {
      objectHandler = syncObjectHandler;
    }
    // TODO: Is it possible to get previous value of a property?
    addListener(object, "_change:bindings:" + key, objectHandler, null, {
      skipChecks: true
    });
    if (!isUndefined && setValueOnBind !== false || setValueOnBind === true) {
      if (debounceSetValueOnBind) {
        debouncedObjectHandler();
      } else {
        syncObjectHandler();
      }
    }
  }
  // add binding data to bindings array
  bindings.push({
    on: on,
    node: node,
    binder: binder,
    objectHandler: objectHandler,
    nodeHandler: nodeHandler,
    syncNodeHandler: syncNodeHandler,
    debouncedNodeHandler: debouncedNodeHandler,
    debouncedObjectHandler: debouncedObjectHandler,
    bindingOptions: bindingOptions
  });
  var extendedEventOptions = assign({
    key: key,
    node: node
  }, eventOptions);
  // DOM listeners ("click::x") follow the node even when the binding is silent
  triggerOne(object, "_bind:" + key, extendedEventOptions);
  // fire events
  if (!silent) {
    triggerOne(object, "bind:" + key, extendedEventOptions);
    triggerOne(object, 'bind', extendedEventOptions);
  }
}